import React, {FC, ReactNode, useState} from "react";
import {Box, Typography} from "@material-ui/core";
import FfairModal from "view/components/modal/FfairModal";
import FfairPaper from "view/components/FfairPaper";
import Form, {ActionButton, Actions} from "view/components/Form";
import RichTextEditor from "view/components/RichTextEditor";
import {hasOversizedFile} from "tools/file/fileSizeLimit";
import {useForm} from "react-hook-form";
import {nanoid} from "nanoid";
import parsePath from "parse-filepath";
import {RawAttachmentFile} from "ts/api/attachmentFile";
import {IStandTaskListRow} from "ts/api/stand-task-list-row";
import {useStyles} from "./styles";
import {RevisionMessageForm} from "./shared";
import ReactionAttachments from "../ReactionAttachments";

export type SubmitRevisionMessageParams = RevisionMessageForm & {
  files: RawAttachmentFile[];
};
type HistoryModalProps = {
  onClose: () => void;
  onSubmit: (data: SubmitRevisionMessageParams) => Promise<void>;
  task: IStandTaskListRow;
  message?: ReactNode;
};

const RevisionModal: FC<HistoryModalProps> = ({
  onClose,
  onSubmit: onSubmitAction,
  task: row,
  message,
}) => {
  const {task} = row;
  const classes = useStyles();
  const [files, setFiles] = useState<RawAttachmentFile[]>([]);

  const form = useForm<RevisionMessageForm>({
    mode: "all",
    defaultValues: {message: ""},
  });

  const onUploadFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const targetFiles = event.target.files;
    if (targetFiles?.length) {
      const uploadedFiles = Array.from(targetFiles).map((file) => ({
        id: nanoid(),
        title: parsePath(file.name).name,
        file,
      }));
      setFiles((prev) => (prev ? [...prev, ...uploadedFiles] : uploadedFiles));
      event.target.value = "";
    }
  };

  const onChangeMessage = (message: string) => {
    form.setValue("message", message, {
      shouldDirty: true,
      shouldValidate: true,
    });
    form.trigger();
  };

  // An over-limit file stays in the list (so the user can see + remove it) but
  // blocks submit and surfaces the inline error below.
  const hasOversizedAttachment = hasOversizedFile(files);
  const isSubmitDisabled =
    !form.formState.isValid ||
    form.formState.isSubmitting ||
    !form.watch("message")?.length ||
    hasOversizedAttachment;

  const onSubmit = form.handleSubmit(async (data) => {
    await onSubmitAction({...data, files});
  });

  return (
    <FfairModal open onClose={onClose}>
      <FfairPaper className={classes.root}>
        <Typography className={classes.taskInfo}>{task.title ?? ""}</Typography>
        <Typography className={classes.heading}>
          Add Revision Request
        </Typography>
        <Form
          form={form}
          onSubmit={onSubmit}
          style={{
            padding: "24px 0 0 0",
          }}
        >
          <RichTextEditor
            id="message"
            label="Message..."
            onChange={onChangeMessage}
          />
          <ReactionAttachments
            inputId="revision-attachment"
            files={files}
            onUpload={onUploadFiles}
            onRemove={(id) =>
              setFiles((prev) => prev.filter((file) => file.id !== id))
            }
          />
          {message && <Box py={2}>{message}</Box>}
          <Actions className={classes.modalActionButtons}>
            <Box />
            <Box>
              <ActionButton
                color="primary"
                text="Cancel"
                onClick={async () => {
                  onClose();
                  setFiles([]);
                }}
              />
              <ActionButton
                className={classes.submitButton}
                disabled={isSubmitDisabled}
                onClick={onSubmit}
                color="primary"
                type="submit"
                variant="contained"
                text="Submit"
              />
            </Box>
          </Actions>
        </Form>
      </FfairPaper>
    </FfairModal>
  );
};

export default RevisionModal;
